import { motion } from "framer-motion";
import { Users, Package, ArrowUpRight } from "lucide-react";
import { IMAGES } from "@/lib/assets";
import { useLang } from "@/lib/i18n";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] } }),
};

const modesMeta = [
  { title: "SQUAD", icon: Users, img: IMAGES.football, testid: "mode-squad" },
  { title: "VAULT", icon: Package, img: IMAGES.tennis, testid: "mode-vault" },
];

export default function TwoSides() {
  const { t } = useLang();
  const modes = modesMeta.map((m, i) => ({ ...m, ...t.two.modes[i] }));
  return (
    <section id="modes" className="py-24 md:py-32 border-b border-white/10">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <motion.div
          variants={reveal}
          custom={0}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="mb-14 md:mb-20 max-w-3xl"
        >
          <p className="font-mono-pw uppercase text-xs tracking-[0.3em] text-[#E6FF00] mb-5">{t.two.overline}</p>
          <h2 className="font-anton uppercase leading-[0.9] text-5xl md:text-7xl">
            {t.two.headA} <br /> <span className="pw-outline-white">{t.two.headB}</span>
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 border border-white/10">
          {modes.map((m, i) => {
            const Icon = m.icon;
            return (
              <motion.div
                key={m.title}
                variants={reveal}
                custom={i + 1}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-80px" }}
                data-testid={m.testid}
                className={`group relative p-8 md:p-12 ${i === 0 ? "md:border-r border-white/10 max-md:border-b" : ""}`}
              >
                <div className="relative h-52 md:h-64 overflow-hidden mb-8 border border-white/10">
                  <img
                    src={m.img}
                    alt={m.title}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] to-transparent" />
                  <Icon className="absolute bottom-4 left-4 text-[#E6FF00]" size={32} strokeWidth={1.5} />
                </div>

                <p className="font-mono-pw uppercase text-xs tracking-[0.25em] text-white/40 mb-3">{m.tag}</p>
                <h3 className="font-anton text-5xl md:text-6xl mb-4 flex items-center gap-3">
                  {m.title}
                  <ArrowUpRight className="text-[#E6FF00] opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all duration-300" size={36} />
                </h3>
                <p className="font-body text-white/70 leading-relaxed mb-7 max-w-md">{m.desc}</p>

                <ul className="space-y-3">
                  {m.points.map((p) => (
                    <li key={p} className="flex items-center gap-3 font-body text-sm text-white/80">
                      <span className="w-1.5 h-1.5 bg-[#E6FF00] shrink-0" />
                      {p}
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
